import { errorToast } from "@/components/ui/toast.tsx";
import { useEffect } from "react";
import { useLocation, useSearch } from "wouter";

/**
 * Global component that checks for OAuth-related error query parameters
 * and displays appropriate toast messages.
 *
 * This handles errors from both base auth and incremental auth flows.
 */
export const GlobalAuthErrorHandler = () => {
  const search = useSearch();
  const [location, setLocation] = useLocation();

  useEffect(() => {
    const searchParams = new URLSearchParams(search);
    const authError = searchParams.get("auth_error");
    const oauthError = searchParams.get("oauth_error");

    let shouldClearParams = false;

    if (authError === "scope_denied") {
      errorToast("Please accept all requested permissions");
      searchParams.delete("auth_error");
      shouldClearParams = true;
    } else if (authError === "account_already_connected") {
      errorToast("That account is already connected to your workspace.");
      searchParams.delete("auth_error");
      shouldClearParams = true;
    } else if (authError === "account_conflict") {
      errorToast("This account is already linked to another user.");
      searchParams.delete("auth_error");
      shouldClearParams = true;
    } else if (authError) {
      errorToast("Sign-in failed. Please try again.");
      searchParams.delete("auth_error");
      shouldClearParams = true;
    } else if (oauthError) {
      const providerNames: Record<string, string> = {
        microsoft: "Outlook",
        google: "Google",
        zoom: "Zoom",
      };
      const displayName =
        providerNames[oauthError] ?? oauthError.charAt(0).toUpperCase() + oauthError.slice(1);
      errorToast(`Failed to connect ${displayName}. Please try again.`);
      searchParams.delete("oauth_error");
      shouldClearParams = true;
    }

    if (shouldClearParams) {
      // Clear error parameters from the URL
      const newSearch = searchParams.toString();
      const pathname = location.split("?")[0];
      const newUrl = newSearch ? `${pathname}?${newSearch}` : pathname;
      setLocation(newUrl, { replace: true });
    }
  }, [search, location, setLocation]);

  return null;
};
